import { Form, Input, message, Modal, Upload } from 'antd';
import cx from 'classnames';
import moment from 'moment';
import 'moment/locale/uk';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../Guard/AuthContext';
import onlineIcon from '../../assets/images/online.png';
import socialsIcon from '../../assets/images/socials.svg';
import Button from '../../components/Button/Button';
import { apiInstance } from '../../utils/api';
import { logUserAction } from '../../utils/logger';

// Іконки для різних секцій форми
const GeneralSalesIcon = () => (
  <img src={socialsIcon} width={30} height={30} alt="Загальний обсяг продажу" />
);

const OnlineSalesIcon = () => (
  <img src={onlineIcon} width={30} height={30} alt="Інтернет продажі" />
);

const FileUploadIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg" fill="#3A4555">
    <circle cx="15" cy="15" r="15" fill="#2CD5C4" opacity="0.9" />
    <g transform="translate(5, 7) scale(0.8)">
      <path
        d="M16.191 0H7.81C4.77 0 3 1.78 3 4.83V15.16C3 18.26 4.77 20 7.81 20H16.191C19.28 20 21 18.26 21 15.16V4.83C21 1.78 19.28 0 16.191 0Z"
        fill="#E8F3FF"
      />
      <path
        d="M8.08094 4.65039V4.66039C7.64994 4.66039 7.30094 5.01039 7.30094 5.44039C7.30094 5.87039 7.64994 6.22039 8.08094 6.22039H11.0699C11.5009 6.22039 11.8509 5.87039 11.8509 5.42939C11.8509 5.00039 11.5009 4.65039 11.0699 4.65039H8.08094ZM15.9209 10.7404H8.08094C7.64994 10.7404 7.30094 10.3904 7.30094 9.9604C7.30094 9.5304 7.64994 9.1794 8.08094 9.1794H15.9209C16.3509 9.1794 16.7009 9.5304 16.7009 9.9604C16.7009 10.3904 16.3509 10.7404 15.9209 10.7404ZM15.9209 15.3104H8.08094C7.78094 15.3504 7.49094 15.2004 7.33094 14.9504C7.17094 14.6904 7.17094 14.3604 7.33094 14.1104C7.49094 13.8504 7.78094 13.7104 8.08094 13.7404H15.9209C16.3199 13.7804 16.6209 14.1204 16.6209 14.5304C16.6209 14.9294 16.3199 15.2704 15.9209 15.3104Z"
        fill="#2CD5C4"
      />
    </g>
  </svg>
);

const CommodityMonthly = ({ activeKey, currentContactStore, onFormSubmit }) => {
  const { setIsStoreSelectDisabled } = useContext(AuthContext);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [clickedSubmit, setClickedSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const [form] = Form.useForm();

  // Автозакриття модалки
  useEffect(() => {
    let autoCloseTimer;

    if (successModal) {
      autoCloseTimer = setTimeout(() => {
        setSuccessModal(false);
        setClickedSubmit(false);
        onFormSubmit();
      }, 3000);
    }

    return () => {
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
      }
    };
  }, [successModal, onFormSubmit]);

  const formatThousands = (numStr) => {
    if (!numStr) return '';
    const normalized = String(numStr).replace(/\s/g, '').replace(',', '.');
    const num = parseFloat(normalized);
    if (isNaN(num)) return numStr;
    const parts = num.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return parts.join('.');
  };

  const formatRealTime = (val) => {
    if (val === undefined || val === null || val === '') return '';
    const str = String(val).replace(',', '.');
    const hasDot = str.includes('.');
    const parts = str.split('.');
    const intPart = parts[0].replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    if (hasDot) {
      const decPart = parts.slice(1).join('').replace(/\D/g, '').slice(0, 2);
      return `${intPart}.${decPart}`;
    }
    return intPart;
  };

  const getNewCursorPos = (oldVal, newVal, oldPos) => {
    let digitsBefore = 0;
    for (let i = 0; i < oldPos && i < oldVal.length; i++) {
      if (/\S/.test(oldVal[i])) digitsBefore++;
    }
    let newPos = 0;
    let count = 0;
    while (newPos < newVal.length && count < digitsBefore) {
      if (/\S/.test(newVal[newPos])) count++;
      newPos++;
    }
    return newPos;
  };

  const handleNumberInput = (e, fieldName) => {
    const input = e.target;
    const { value, selectionStart } = input;

    const formatted = formatRealTime(value);
    const newCursor = getNewCursorPos(value, formatted, selectionStart);

    form.setFieldsValue({ [fieldName]: formatted });

    if (['tradeWithoutVat', 'tradeWithVat', 'vat'].includes(fieldName)) {
      form.validateFields(['vat']).catch(() => {});
    }
    if (['onlineTradeWithoutVat', 'onlineTradeWithVat', 'onlineVat'].includes(fieldName)) {
      form.validateFields(['onlineVat']).catch(() => {});
    }

    setTimeout(() => {
      input.setSelectionRange(newCursor, newCursor);
    }, 0);
  };

  const handleNumberBlur = (e, fieldName) => {
    let value = e.target.value;
    if (!value) {
      if (['vat', 'onlineVat'].includes(fieldName)) {
        form.setFieldsValue({ [fieldName]: '0.00' });
        if (fieldName === 'vat') {
          form.validateFields(['vat']).catch(() => {});
        } else {
          form.validateFields(['onlineVat']).catch(() => {});
        }
      }
      return;
    }

    const formatted = formatThousands(value);
    form.setFieldsValue({ [fieldName]: formatted });
    form.validateFields([fieldName]).catch(() => {});

    if (['tradeWithoutVat', 'tradeWithVat', 'vat'].includes(fieldName)) {
      form.validateFields(['vat']).catch(() => {});
    }
    if (['onlineTradeWithoutVat', 'onlineTradeWithVat', 'onlineVat'].includes(fieldName)) {
      form.validateFields(['onlineVat']).catch(() => {});
    }
  };

  const validateNumberInput = (event) => {
    const { value, selectionStart } = event.target;
    const key = event.key;

    // Разрешаем только цифры, точку, запятую и пробелы
    if (!/[0-9.,\s]/.test(key)) {
      event.preventDefault();
      return;
    }

    // Разрешаем только один разделитель (точку или запятую)
    if ((key === '.' || key === ',') && (value.includes('.') || value.includes(','))) {
      event.preventDefault();
      return;
    }

    // Разрешаем не более двух цифр после разделителя
    const sepIndex = Math.max(value.indexOf('.'), value.indexOf(','));
    if (sepIndex !== -1) {
      const decimal = value.slice(sepIndex + 1);
      const cursorAfterSep = selectionStart > sepIndex;

      if (decimal && decimal.length >= 2 && cursorAfterSep && key !== '.' && key !== ',') {
        event.preventDefault();
        return;
      }
    }

    if (value === '' && key === '0') {
      return;
    }

    if (value === '0' && /[0-9]/.test(key)) {
      event.preventDefault();
      return;
    }
  };

  // Запрещаем вставку текста
  const preventPaste = (e) => {
    e.preventDefault();
    message.error('Вставка тексту заборонена!');
  };

  const validateFloat = (value) => {
    if (value === undefined || value === null || value === '') return false;
    const normalized = String(value).replace(/\s/g, '').replace(',', '.');
    const regex = /^\d+(\.\d{0,2})?$/;
    return regex.test(normalized);
  };

  const floatRule = (messageText) => [
    { required: true, message: messageText },
    {
      validator(_, value) {
        if (!value || validateFloat(value)) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('Введіть коректну суму (до 2 знаків після коми)'));
      },
    },
  ];

  const vatRule = (withoutVatFieldName, withVatFieldName) => [
    {
      validator(_, value) {
        if (value && !validateFloat(value)) {
          return Promise.reject(new Error('Введіть коректну суму (до 2 знаків після коми)'));
        }
        // Валідацію рівності суми ПДВ тимчасово вимкнено
        return Promise.resolve();
      },
    },
  ];


  const formatNumberValue = (val) => {
    if (val === undefined || val === null || val === '') return null;
    const normalized = String(val).replace(/\s/g, '').replace(',', '.');
    const num = parseFloat(normalized);
    return isNaN(num) ? val : num.toFixed(2);
  };

  const oneMounthAgo = moment().subtract(1, 'months').format('YYYY-MM-DD HH:mm:ss');

  // ====== Хендлер файлу для основного звіту ======
  const handleFileUpload = (info) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }

    if (info.file.status === 'done' || info.file.originFileObj) {
      const file = info.file.originFileObj || info.file;
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        form.setFieldsValue({
          monthlyReport: {
            attachFile1: reader.result,
            attachFileName1: file.name,
          },
        });
        setFileList([
          {
            uid: file.uid || '-1',
            name: file.name,
            status: 'done',
          },
        ]);
        form.setFields([{ name: 'monthlyReport', errors: [] }]);
        setLoading(false);
      };
    }
  };

  const beforeUpload = (file) => {
    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    const isAllowedType = allowedTypes.includes(file.type);
    if (!isAllowedType) {
      const errMsg = 'Можна завантажувати лише JPG, PNG, PDF, DOC, DOCX, XLS або XLSX файли!';
      message.error(errMsg);
      logUserAction({
        action: 'FILE_UPLOAD_REJECTED',
        level: 'warn',
        message: errMsg,
        storeCrmId: currentContactStore?.crmId,
        details: { fileName: file.name, fileSize: file.size, fileType: file.type },
      });
      form.setFieldsValue({ monthlyReport: null });
      form.setFields([{ name: 'monthlyReport', errors: [errMsg] }]);
      setFileList([]);
      return false;
    }
    const isLt20M = file.size / 1024 / 1024 <= 20;
    if (!isLt20M) {
      const errMsg = 'Розмір файлу не повинен перевищувати 20MB!';
      message.error(errMsg);
      logUserAction({
        action: 'FILE_UPLOAD_REJECTED',
        level: 'warn',
        message: errMsg,
        storeCrmId: currentContactStore?.crmId,
        details: { fileName: file.name, fileSize: file.size, fileType: file.type },
      });
      form.setFieldsValue({ monthlyReport: null });
      form.setFields([{ name: 'monthlyReport', errors: [errMsg] }]);
      setFileList([]);
      return false;
    }
    return true;
  };

  const onFinish = async (values) => {
    setClickedSubmit(true);
    setIsStoreSelectDisabled?.(true);

    const targetStore = currentContactStore;
    const storeCrmId = targetStore?.crmId;

    if (!storeCrmId) {
      message.error('Торгову точку не вибрано!');
      setClickedSubmit(false);
      setIsStoreSelectDisabled?.(false);
      return;
    }

    if (!values.monthlyReport?.attachFile1) {
      const errMsg = 'Завантажте файл місячного звіту!';
      message.error(errMsg);
      logUserAction({
        action: 'SUBMIT_TURNOVER_MONTHLY_VALIDATION_FAILED',
        level: 'warn',
        message: errMsg,
        storeCrmId,
      });
      form.setFields([{ name: 'monthlyReport', errors: [errMsg] }]);
      setError(true);
      setSuccess(false);
      setClickedSubmit(false);
      setIsStoreSelectDisabled?.(false);
      return;
    }

    // Формуємо єдиний payload з усіма даними (форматируем числа для Creatio)
    const payload = {
      Id: null,
      accountId: storeCrmId,
      storeCrmId: storeCrmId,
      SuAmountWithoutVAT: formatNumberValue(values.tradeWithoutVat),
      SuAmountWithVAT: formatNumberValue(values.tradeWithVat),
      SuName: 'Щомісячний звіт',
      SuPeriod: null,
      SuReportType: 'cb55b739-ab80-4372-be02-fcb7ab24315f',
      RespCash: formatNumberValue(values.cash) || 0,
      RespExciseTax: formatNumberValue(values.exciseTax) || 0,
      RespVAT: formatNumberValue(values.vat || 0),
      SuDate: oneMounthAgo,
      RespOnlineTurnover: false,
      attachFile1: values.monthlyReport.attachFile1,
      attachFileName1: values.monthlyReport.attachFileName1,
    };

    if (targetStore?.online) {
      payload.online_SuAmountWithoutVAT = formatNumberValue(values.onlineTradeWithoutVat);
      payload.online_SuAmountWithVAT = formatNumberValue(values.onlineTradeWithVat);
      payload.online_RespVAT = formatNumberValue(values.onlineVat || 0);

      payload.online_name = 'Щомісячний звіт (Онлайн-продаж)';
    }

    console.log('Payload being sent:', payload);

    apiInstance(
      `${process.env.REACT_APP_API_URL}/api/post-data?type=trade-turnover-mountly`,
      'POST',
      payload
    )
      .then(() => {
        logUserAction({
          action: 'SUBMIT_TURNOVER_MONTHLY_SUCCESS',
          level: 'info',
          message: 'Успішно відправлено місячний товарообіг',
          storeCrmId,
          details: {
            tradeWithoutVat: values.tradeWithoutVat,
            tradeWithVat: values.tradeWithVat,
            vat: values.vat || '0.00',
          },
        });
        setSuccessModal(true);
        form.resetFields();
        setFileList([]);
        setSuccess(true);
        setError(false);
      })
      .catch((error) => {
        console.error('Error sending payload:', error);
        logUserAction({
          action: 'SUBMIT_TURNOVER_MONTHLY_ERROR',
          level: 'error',
          message: error?.message || 'Помилка відправки місячного товарообігу',
          storeCrmId,
          details: { error: String(error) },
        });
        setError(true);
        setSuccess(false);
      })
      .finally(() => {
        setClickedSubmit(false);
        setIsStoreSelectDisabled?.(false);
      });
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Validation failed:', errorInfo);
    message.error('Перевірте правильність заповнення полів та завантажений файл!');
    logUserAction({
      action: 'SUBMIT_TURNOVER_MONTHLY_VALIDATION_FAILED',
      level: 'warn',
      message: 'Помилка валідації форми місячного товарообігу',
      storeCrmId: currentContactStore?.crmId,
      details: {
        errorFields: errorInfo?.errorFields?.map((f) => ({
          name: f.name,
          errors: f.errors,
        })),
      },
    });
    setError(true);
    setSuccess(false);
    setClickedSubmit(false);
  };


  const renderCommodityFooter = useMemo(() => {
    return (
      <div
        className={cx(
          'commodity-form__footer-title',
          { success: success && !error },
          { error: error && !success }
        )}
      >
        {success && !error && 'Дані було успішно відправлено'}
        {error && !success}
      </div>
    );
  }, [success, error]);

  return (
    <>
      <div className="commodity__title text-black-bold-f19-l33">
        Звіт за {moment().subtract(1, 'month').locale('ua').format('MMMM YYYY')} року
      </div>
      <div className="commodity__text text-grey-normal-f16-l28">
        Будь ласка, заповніть усі необхідні поля для створення звіту за місяць
      </div>{' '}
      <Form
        className="commodity-form"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        form={form}
        autoComplete="off"
        requiredMark={false}
      >
        {/* Один ряд з трьома колонками */}
        <div className="commodity-form__row">
          {/* Перша колонка - Загальні продажі */}
          <div
            className={`commodity-form__col ${
              !currentContactStore.online ? 'commodity-form__col--wider' : ''
            }`}
          >
            <div className="commodity-form__section-header">
              <GeneralSalesIcon />{' '}
              <span className="commodity-form__section-title">Загальний обсяг продажу</span>
            </div>
            <Form.Item
              name="tradeWithoutVat"
              label="Сумарний товарообіг за місяць (без ПДВ):"
              rules={floatRule('Введіть сумарний товарообіг за місяць (без ПДВ)')}
            >
              <Input
                placeholder="Введіть суму"
                suffix={<span className="text-grey-normal-f16-l28">грн</span>}
                onChange={(e) => handleNumberInput(e, 'tradeWithoutVat')}
                onBlur={(e) => handleNumberBlur(e, 'tradeWithoutVat')}
                onKeyPress={validateNumberInput}
                onPaste={preventPaste}
              />
            </Form.Item>
            {currentContactStore.cash && (
              <Form.Item
                name="cash"
                label="Готівка:"
                rules={floatRule('Введіть суму готівки')}
              >
                <Input
                  placeholder="Введіть суму"
                  suffix={<span className="text-grey-normal-f16-l28">грн</span>}
                  onChange={(e) => handleNumberInput(e, 'cash')}
                  onBlur={(e) => handleNumberBlur(e, 'cash')}
                  onKeyPress={validateNumberInput}
                  onPaste={preventPaste}
                />
              </Form.Item>
            )}
            <Form.Item
              name="vat"
              label="Податок на додану вартість (ПДВ):"
              rules={vatRule('tradeWithoutVat', 'tradeWithVat')}
            >
              <Input
                placeholder="Введіть суму"
                suffix={<span className="text-grey-normal-f16-l28">грн</span>}
                onChange={(e) => handleNumberInput(e, 'vat')}
                onBlur={(e) => handleNumberBlur(e, 'vat')}
                onKeyPress={validateNumberInput}
                onPaste={preventPaste}
              />
            </Form.Item>
            <Form.Item
              name="tradeWithVat"
              label="Сумарний товарообіг за місяць (з ПДВ):"
              rules={floatRule('Введіть сумарний товарообіг за місяць (з ПДВ)')}
            >
              <Input
                placeholder="Введіть суму"
                suffix={<span className="text-grey-normal-f16-l28">грн</span>}
                onChange={(e) => handleNumberInput(e, 'tradeWithVat')}
                onBlur={(e) => handleNumberBlur(e, 'tradeWithVat')}
                onKeyPress={validateNumberInput}
                onPaste={preventPaste}
              />
            </Form.Item>
            {currentContactStore.excise && (
              <Form.Item
                name="exciseTax"
                label="Акцизний збір:"
                rules={floatRule('Введіть суму акцизного збору')}
              >
                <Input
                  placeholder="Введіть суму"
                  suffix={<span className="text-grey-normal-f16-l28">грн</span>}
                  onChange={(e) => handleNumberInput(e, 'exciseTax')}
                  onBlur={(e) => handleNumberBlur(e, 'exciseTax')}
                  onKeyPress={validateNumberInput}
                  onPaste={preventPaste}
                />
              </Form.Item>
            )}
          </div>

          {/* Друга колонка - Онлайн-продаж (тільки якщо є) */}
          {currentContactStore.online && (
            <div className="commodity-form__col">
              <div className="commodity-form__section-header">
                <OnlineSalesIcon />{' '}
                <span className="commodity-form__section-title">Онлайн-продаж</span>
              </div>
              <Form.Item
                name="onlineTradeWithoutVat"
                label="Сумарний товарообіг за місяць (без ПДВ):"
                rules={floatRule('Введіть сумарний товарообіг за місяць (без ПДВ)')}
              >
                <Input
                  placeholder="Введіть суму"
                  suffix={<span className="text-grey-normal-f16-l28">грн</span>}
                  onChange={(e) => handleNumberInput(e, 'onlineTradeWithoutVat')}
                  onBlur={(e) => handleNumberBlur(e, 'onlineTradeWithoutVat')}
                  onKeyPress={validateNumberInput}
                  onPaste={preventPaste}
                />
              </Form.Item>
              <Form.Item
                name="onlineVat"
                label="Податок на додану вартість (ПДВ):"
                rules={vatRule('onlineTradeWithoutVat', 'onlineTradeWithVat')}
              >
                <Input
                  placeholder="Введіть суму"
                  suffix={<span className="text-grey-normal-f16-l28">грн</span>}
                  onChange={(e) => handleNumberInput(e, 'onlineVat')}
                  onBlur={(e) => handleNumberBlur(e, 'onlineVat')}
                  onKeyPress={validateNumberInput}
                  onPaste={preventPaste}
                />
              </Form.Item>
              <Form.Item
                name="onlineTradeWithVat"
                label="Сумарний товарообіг за місяць (з ПДВ):"
                rules={floatRule('Введіть сумарний товарообіг за місяць (з ПДВ)')}
              >
                <Input
                  placeholder="Введіть суму"
                  suffix={<span className="text-grey-normal-f16-l28">грн</span>}
                  onChange={(e) => handleNumberInput(e, 'onlineTradeWithVat')}
                  onBlur={(e) => handleNumberBlur(e, 'onlineTradeWithVat')}
                  onKeyPress={validateNumberInput}
                  onPaste={preventPaste}
                />
              </Form.Item>
            </div>
          )}

          {/* Третя колонка - Завантаження файлу основного звіту */}
          <div className="commodity-form__col commodity-form__col--file">
            <div className="commodity-form__section-header">
              <FileUploadIcon />{' '}
              <span className="commodity-form__section-title">Завантаження файлу</span>
            </div>{' '}
            <Form.Item
              name="monthlyReport"
              label="Місячний звіт (jpg, png, pdf, doc, docx, xls, xlsx):"
              rules={[
                { required: true, message: 'Завантажте файл місячного звіту' },
                {
                  validator(_, value) {
                    if (value && value.attachFile1) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error('Будь ласка, завантажте коректний файл (до 20MB)')
                    );
                  },
                },
              ]}
            >
              <Upload
                fileList={fileList}
                onRemove={() => {
                  setFileList([]);
                  form.setFieldsValue({ monthlyReport: null });
                  form.setFields([
                    {
                      name: 'monthlyReport',
                      errors: ['Завантажте файл місячного звіту'],
                    },
                  ]);
                }}
                beforeUpload={(file) => {
                  if (beforeUpload(file)) {
                    handleFileUpload({
                      file: {
                        ...file,
                        status: 'done',
                        originFileObj: file,
                      },
                      fileList: [file],
                    });
                  }
                  return false;
                }}
                className="commodity-form__upload"
                maxCount={1}
                multiple={false}
                accept="image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              >
                Виберіть файл
              </Upload>
            </Form.Item>
            <Form.Item>
              <Button
                type="submit"
                bg="pink"
                disabled={clickedSubmit}
                className="commodity-form__submit-button"
              >
                Надіслати
              </Button>
            </Form.Item>
          </div>
        </div>

        <div className="commodity-form__footer">{(error || success) && renderCommodityFooter}</div>
      </Form>
      <Modal
        className="success-modal"
        open={successModal}
        footer={null}
        onCancel={() => {
          setSuccessModal(false);
          setClickedSubmit(false);
          onFormSubmit();
        }}
      >
        <div className="success-modal__wrapper">
          <div className="text-black-medium-f22-l30">Дякуємо</div>
          <div className="text-grey-dark-normal-f16-l28">Місячний товарообіг успішно надіслано</div>
        </div>
      </Modal>
    </>
  );
};

export default CommodityMonthly;
